import { Box, Divider, Flex, Text } from '@chakra-ui/react'
import { Button, CreditCardIcon, StarIcon, SwatchIcon } from 'components'
import { useWorkspace } from 'components/providers'
import dayjs from 'dayjs'
import { SubscriptionPlan } from 'types'
import { sendIntercomMessage, useBreakpoint } from 'utils'

export default function SubscriptionInfo() {
  const { workspace } = useWorkspace()
  const breakpoint = useBreakpoint()

  if (!workspace?.subscription) return null

  const { plan, billingFrequency, isTrial, nextPaymentDueDate } = workspace.subscription
  const paymentDueDate = dayjs(nextPaymentDueDate)
  const subtractUnits = billingFrequency === 'yearly' ? 'year' : 'month'
  const dateFormatter = breakpoint > 1 ? 'MMM D, YYYY' : 'MMM D, YY'
  const formattedStartDate = paymentDueDate.subtract(1, subtractUnits).format(dateFormatter)
  const formattedEndDate = paymentDueDate.subtract(1, 'day').format(dateFormatter)
  const billingPeriod = formattedStartDate + ' - ' + formattedEndDate
  const { title, description, details } = planInfo[plan]

  return (
    <Flex
      borderRadius="sm"
      borderColor="rgba(23, 23, 23, 0.1)"
      backgroundColor="white"
      borderStyle="solid"
      borderWidth="1px"
      p={{ base: 4, md: 8 }}
      gap={8}
      flexDirection="column"
    >
      <Flex alignSelf="stretch" justifyContent="space-between" alignItems="center">
        <Text textStyle="h5">Current Subscription</Text>
      </Flex>

      <Flex flexDirection="column" gap={5}>
        <Box
          alignSelf="flex-start"
          borderRadius="999px"
          px={4}
          py={2}
          background={
            isTrial ? 'primary.500' : 'linear-gradient(308deg, #41C19E 25.65%, #1B379C 87.76%)'
          }
        >
          <Text color="#FFF" textStyle="h5">
            {isTrial ? (
              <>
                <strong>Trial: </strong>
                {dayjs(nextPaymentDueDate).diff(dayjs(), 'day') + 1} Days Left
              </>
            ) : (
              title
            )}
          </Text>
        </Box>

        <Flex gap={4} flexDirection="column">
          <Flex gap={2} flexDirection="column">
            {!isTrial && <Text textStyle="h6">{description}</Text>}
            <Text textStyle="bodyMedium" opacity={0.8}>
              {isTrial
                ? `We hope you're enjoying your trial! Contact us if you have any questions or are ready to upgrade.`
                : details}
            </Text>
          </Flex>

          <Divider />

          {!isTrial && (
            <Flex alignItems="center" gap={2}>
              <Box
                borderRadius="999px"
                textStyle="bodySmall"
                bg="#0FBA00"
                px={4}
                whiteSpace="nowrap"
                py={2}
                color="#FFF"
              >
                Billed: {billingFrequency === 'monthly' ? 'Monthly' : 'Annual'}
              </Box>
              <Flex columnGap={2} wrap="wrap">
                <Text color="#171717" textStyle="bodySmall" opacity="0.6" my="auto">
                  Current Billing Period:
                </Text>
                <Text color="#171717" textStyle="bodySmall" opacity="0.6" my="auto">
                  {billingPeriod}
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>

      <Flex gap={3} wrap="wrap">
        <Button
          borderRadius="md"
          leftIcon={<StarIcon color="primary.500" />}
          onClick={() =>
            sendIntercomMessage('Hello, I am interested in upgrading my current subscription.')
          }
          size="lg"
          variant="outline"
        >
          Upgrade {breakpoint > 1 ? ' Plan' : ''}
        </Button>
        <Button
          borderRadius="md"
          leftIcon={<SwatchIcon />}
          onClick={() => window.open('https://www.momentifyapp.com/#Plans', '_blank')}
          size="lg"
          variant="outline"
        >
          View Plans
        </Button>
        <Button
          variant="outline"
          size="lg"
          isDisabled
          borderRadius="md"
          leftIcon={<CreditCardIcon />}
        >
          Manage Billing
        </Button>
      </Flex>
    </Flex>
  )
}

const planInfo: Record<SubscriptionPlan, { title: string; description: string; details: string }> =
  {
    basic: {
      title: 'Basic',
      description: 'Essentials to Create Engaging Experiences and Capture Key Insights.',
      details:
        'Perfect for small teams managing events or facilities, with a focus on engagement, lead capture, and valuable attendee interactions.',
    },
    pro: {
      title: 'Pro',
      description: 'Enhanced Tools to Elevate Engagement and Maximize ROX.',
      details:
        'Ideal for growing businesses managing multiple events, facilities, or both, with real-time insights and smarter lead conversion.',
    },
    enterprise: {
      title: 'Enterprise',
      description: 'Comprehensive ROX Solutions for Large-Scale Events and Facilities.',
      details:
        'Built for enterprises handling complex events, multi-location facilities, or integrated experiences, with advanced insights and full customization.',
    },
  }
